if (!customElements.get('blog-article-list')) {
  customElements.define(
    'blog-article-list',
    class BlogArticleList extends HTMLElement {
      connectedCallback() {
        this.pageParameter = this.dataset.pageParameter || 'article_page';

        if (this.migrateNativePageParameter()) return;

        this.articles = Array.from(this.querySelectorAll('[data-blog-article]'));
        this.paginationContainer = this.querySelector('[data-blog-pagination]');
        this.pageSize = Number.parseInt(this.dataset.pageSize, 10) || 6;
        this.pageCount = Math.ceil(this.articles.length / this.pageSize);
        this.currentPage = this.getCurrentPage();

        this.normalisePageParameter();
        this.renderArticles();
        this.renderPagination();
      }

      migrateNativePageParameter() {
        const nativePage = Number.parseInt(this.dataset.nativePage, 10);
        const url = new URL(window.location.href);

        if (nativePage <= 1 && !url.searchParams.has('page')) return false;

        if (!url.searchParams.has(this.pageParameter)) {
          url.searchParams.set(this.pageParameter, url.searchParams.get('page') || nativePage);
        }

        url.searchParams.delete('page');
        window.location.replace(url.toString());

        return true;
      }

      getCurrentPage() {
        const requestedPage = this.parsePageValue(
          new URLSearchParams(window.location.search).get(this.pageParameter)
        );

        if (requestedPage === null) return 1;

        return Math.min(requestedPage, Math.max(this.pageCount, 1));
      }

      normalisePageParameter() {
        const url = new URL(window.location.href);
        const requestedPage = this.parsePageValue(url.searchParams.get(this.pageParameter));

        if (!url.searchParams.has(this.pageParameter)) return;
        if (requestedPage === this.currentPage && this.currentPage > 1) return;

        if (this.currentPage === 1) {
          url.searchParams.delete(this.pageParameter);
        } else {
          url.searchParams.set(this.pageParameter, this.currentPage);
        }

        window.history.replaceState({}, '', url.toString());
      }

      parsePageValue(value) {
        if (!/^[1-9]\d*$/.test(value || '')) return null;

        const page = Number(value);

        return Number.isSafeInteger(page) ? page : null;
      }

      renderArticles() {
        const firstArticleIndex = (this.currentPage - 1) * this.pageSize;
        const lastArticleIndex = firstArticleIndex + this.pageSize;

        this.articles.forEach((article, index) => {
          const isVisible = index >= firstArticleIndex && index < lastArticleIndex;
          article.hidden = !isVisible;

          if (isVisible) {
            article.style.setProperty('--animation-order', index - firstArticleIndex + 1);
          }
        });
      }

      renderPagination() {
        if (!this.paginationContainer || this.pageCount <= 1) return;

        const navigation = document.createElement('nav');
        const list = document.createElement('ul');

        this.paginationContainer.hidden = false;
        this.paginationContainer.className = 'pagination-wrapper';
        navigation.className = 'pagination';
        navigation.setAttribute('role', 'navigation');
        navigation.setAttribute('aria-label', this.dataset.paginationLabel || 'Pagination');
        list.className = 'pagination__list list-unstyled';
        list.setAttribute('role', 'list');

        if (this.currentPage > 1) {
          list.append(this.createArrowItem(this.currentPage - 1, 'previous'));
        }

        this.getPaginationParts().forEach((page) => {
          list.append(page === null ? this.createEllipsisItem() : this.createPageItem(page));
        });

        if (this.currentPage < this.pageCount) {
          list.append(this.createArrowItem(this.currentPage + 1, 'next'));
        }

        navigation.append(list);
        this.paginationContainer.replaceChildren(navigation);
      }

      getPaginationParts() {
        if (this.pageCount <= 7) {
          return Array.from({ length: this.pageCount }, (_, index) => index + 1);
        }

        const pages = new Set([1, this.pageCount]);

        for (
          let page = Math.max(2, this.currentPage - 1);
          page <= Math.min(this.pageCount - 1, this.currentPage + 1);
          page += 1
        ) {
          pages.add(page);
        }

        if (this.currentPage <= 4) {
          [2, 3, 4, 5].forEach((page) => pages.add(page));
        }

        if (this.currentPage >= this.pageCount - 3) {
          [this.pageCount - 4, this.pageCount - 3, this.pageCount - 2, this.pageCount - 1].forEach((page) =>
            pages.add(page)
          );
        }

        return Array.from(pages)
          .sort((firstPage, secondPage) => firstPage - secondPage)
          .flatMap((page, index, sortedPages) => {
            if (index === 0 || page === sortedPages[index - 1] + 1) return [page];

            return [null, page];
          });
      }

      createPageItem(page) {
        const item = document.createElement('li');
        const link = document.createElement('a');

        link.className = `pagination__item${page === this.currentPage ? ' pagination__item--current light' : ' link'}`;
        link.setAttribute('aria-label', this.getPageLabel(page));
        link.textContent = page;

        if (page === this.currentPage) {
          link.setAttribute('role', 'link');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('aria-current', 'page');
        } else {
          link.href = this.getPageUrl(page);
        }

        item.append(link);
        return item;
      }

      createEllipsisItem() {
        const item = document.createElement('li');
        const ellipsis = document.createElement('span');

        ellipsis.className = 'pagination__item';
        ellipsis.textContent = '…';
        item.append(ellipsis);

        return item;
      }

      createArrowItem(page, direction) {
        const item = document.createElement('li');
        const link = document.createElement('a');

        link.href = this.getPageUrl(page);
        link.className = `pagination__item pagination__item--${direction === 'previous' ? 'next' : 'prev'} pagination__item-arrow link motion-reduce`;
        link.setAttribute(
          'aria-label',
          direction === 'previous' ? this.dataset.previousLabel || 'Previous page' : this.dataset.nextLabel || 'Next page'
        );
        link.innerHTML = `
          <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
          </svg>
        `;

        item.append(link);
        return item;
      }

      getPageLabel(page) {
        return (this.dataset.pageLabel || 'Page [page]').replace('[page]', page);
      }

      getPageUrl(page) {
        const url = new URL(window.location.href);

        url.searchParams.delete('page');

        if (page === 1) {
          url.searchParams.delete(this.pageParameter);
        } else {
          url.searchParams.set(this.pageParameter, page);
        }

        return url.toString();
      }
    }
  );
}
